import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Box,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import DateField from 'components/fields/DateField';
import SelectSearch from 'components/fields/SelectSearch';
import { useAppSelector, useAppDispatch } from 'hooks';
import { useCarriers } from 'hooks/use-carriers-hook';
import {
  setBinderNumber,
  setEffectiveDate,
  setInsuranceCompany,
  setPremium,
  useLegacyAutoFormLocked,
} from 'reducers/legacyAutoFormReducer';

interface BinderIdentificationSectionProps {
  canEditPremium?: boolean;
}

const BinderIdentificationSection = ({
  canEditPremium = false,
}: BinderIdentificationSectionProps) => {
  const dispatch = useAppDispatch();
  const isLocked = useLegacyAutoFormLocked();

  const binderNumber = useAppSelector(
    (state) => state.legacyAutoForm.data.binder_information.binder_number,
  );
  const effectiveDate = useAppSelector(
    (state) => state.legacyAutoForm.data.binder_information.effective_date,
  );
  const insuranceCompany = useAppSelector(
    (state) => state.legacyAutoForm.data.binder_information.insurance_company,
  );
  const premium = useAppSelector(
    (state) => state.legacyAutoForm.data.binder_information.premium,
  );

  const { carrierOptions, isLoadingCarriers } = useCarriers();

  const handleBinderNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setBinderNumber(event.target.value));
  };

  const handleEffectiveDateChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dispatch(setEffectiveDate(`${year}-${month}-${day}`));
    } else {
      dispatch(setEffectiveDate(''));
    }
  };

  const parseEffectiveDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(
      parseInt(match[1]),
      parseInt(match[2]) - 1,
      parseInt(match[3]),
    );
  };

  const handleInsuranceCompanyChange = (value: string | number | null) => {
    if (value) {
      const selectedCarrier = carrierOptions.find(
        (option) => option.value === value,
      );
      dispatch(setInsuranceCompany(selectedCarrier?.label || ''));
    } else {
      dispatch(setInsuranceCompany(''));
    }
  };

  const selectedCarrierId =
    carrierOptions.find((option) => option.label === insuranceCompany)?.value ||
    null;

  const handlePremiumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value === '') {
      dispatch(setPremium(''));
      return;
    }

    const decimalRegex = /^(\d+\.?\d*|\.\d*)$/;
    if (decimalRegex.test(value)) {
      dispatch(setPremium(value));
    }
  };

  return (
    <Card mb="20px" id="sectionBinderIdentification">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Binder Identification
      </Text>

      <VStack spacing={4} align="stretch">
        <HStack spacing={4} align="stretch">
          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Binder Number</FormLabel>
            <Input
              variant="main"
              value={binderNumber}
              onChange={handleBinderNumberChange}
              placeholder="Enter binder number"
              isDisabled={isLocked}
            />
          </FormControl>

          <Box flex={1}>
            <DateField
              label="Effective Date"
              date={parseEffectiveDate(effectiveDate)}
              onChange={handleEffectiveDateChange}
              required
              isDisabled={isLocked}
            />
          </Box>

          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Insurance Company</FormLabel>
            <SelectSearch
              options={carrierOptions}
              value={selectedCarrierId}
              onChange={handleInsuranceCompanyChange}
              placeholder="Select insurance company"
              isLoading={isLoadingCarriers}
              allowClear
              isDisabled={isLocked}
            />
          </FormControl>

          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Premium</FormLabel>
            <Input
              variant="main"
              value={premium}
              onChange={handlePremiumChange}
              placeholder="Enter premium amount"
              isDisabled={isLocked && !canEditPremium}
            />
          </FormControl>
        </HStack>
      </VStack>
    </Card>
  );
};

export default BinderIdentificationSection;
